import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { AlertCircle, Building2, ChevronRight, RefreshCw, ArrowLeft, Lock, Shield } from 'lucide-react'

interface TenantOption {
  id: string
  slug: string
  name: string | null
  role: string
}

type VerifyStep = 'validating' | 'tenant' | 'code' | '2fa' | 'locked' | 'error'

/**
 * Magic Link Verification Page
 * Handles steps 2-4 of the auth flow:
 * 1. Validate magic link token
 * 2. Show tenant picker (if multiple tenants)
 * 3. Request and enter PIN
 */
export default function MagicLinkVerify() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const [step, setStep] = useState<VerifyStep>('validating')
  const [email, setEmail] = useState('')
  const [tenants, setTenants] = useState<TenantOption[]>([])
  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(null)
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [sessionToken, setSessionToken] = useState('')
  const [mfaSessionToken, setMfaSessionToken] = useState('')
  const [twoFACode, setTwoFACode] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [lockoutMinutes, setLockoutMinutes] = useState(0)

  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([])
  const twoFAInputRefs = useRef<(HTMLInputElement | null)[]>([])

  const { validateMagicLink, setTenant, storeAvailableTenants, requestCode, verifyCode, verify2FA, resendCode, checkLockoutStatus } = useAuth()

  // Validate magic link on mount
  useEffect(() => {
    if (!token) {
      setError('Invalid sign-in link')
      setStep('error')
      return
    }

    const validate = async () => {
      try {
        const result = await validateMagicLink(token)
        setEmail(result.email)
        setTenants(result.tenants)

        // Store available tenants for tenant switcher (even if single tenant)
        if (result.tenants.length > 0) {
          storeAvailableTenants(result.tenants)
        }

        if (result.tenants.length === 0) {
          // No tenants associated with this email
          setError('No organizations found for this email address. Please contact your administrator.')
          setStep('error')
        } else if (result.tenants.length === 1) {
          // Single tenant - auto-select and proceed to code
          const tenant = result.tenants[0]
          setSelectedTenant(tenant)
          setTenant({ slug: tenant.slug, name: tenant.name })
          // Pass email directly since setState is async
          await proceedToCodeStep(tenant, result.email)
        } else {
          // Multiple tenants - show picker
          setStep('tenant')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Invalid or expired sign-in link')
        setStep('error')
      }
    }

    validate()
  }, [token])

  // Handle resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Focus first code input when entering code step
  useEffect(() => {
    if (step === 'code' && codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus()
    }
    if (step === '2fa' && twoFAInputRefs.current[0]) {
      twoFAInputRefs.current[0].focus()
    }
  }, [step])

  const proceedToCodeStep = async (tenant: TenantOption, emailToUse?: string) => {
    setIsLoading(true)
    setError('')
    try {
      // Set tenant context for API calls
      setTenant({ slug: tenant.slug, name: tenant.name })

      // Check if account is locked before requesting code
      const lockoutStatus = await checkLockoutStatus(emailToUse || email)
      if (lockoutStatus.locked) {
        setLockoutMinutes(lockoutStatus.remaining_minutes || 30)
        setStep('locked')
        setIsLoading(false)
        return
      }

      // Request PIN code (use passed email or state)
      const result = await requestCode(emailToUse || email)
      setSessionToken(result.session_token)
      setStep('code')
      setResendCooldown(30)
    } catch (err) {
      // Check if the error is a lockout error (423 status)
      const errorMessage = err instanceof Error ? err.message : 'Failed to send verification code'
      if (errorMessage.includes('locked') || errorMessage.includes('Too many failed attempts')) {
        const match = errorMessage.match(/(\d+) (?:more )?minutes?/i)
        setLockoutMinutes(match ? parseInt(match[1]) : 30)
        setStep('locked')
      } else {
        setError(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleTenantSelect = async (tenant: TenantOption) => {
    setSelectedTenant(tenant)
    await proceedToCodeStep(tenant)
  }

  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)

    const newCode = [...code]
    newCode[index] = digit
    setCode(newCode)

    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus()
    }

    if (digit && index === 5) {
      const fullCode = newCode.join('')
      if (fullCode.length === 6) {
        handleCodeSubmit(fullCode)
      }
    }
  }

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus()
    }
  }

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 6) {
      const newCode = pastedData.split('')
      setCode(newCode)
      codeInputRefs.current[5]?.focus()
      handleCodeSubmit(pastedData)
    }
  }

  const handleCodeSubmit = async (fullCode?: string) => {
    const codeToVerify = fullCode || code.join('')
    if (codeToVerify.length !== 6) {
      setError('Please enter the complete 6-digit code')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      const result = await verifyCode(sessionToken, codeToVerify)

      // Check if 2FA is required
      if (result.requires_2fa && result.mfa_session_token) {
        setMfaSessionToken(result.mfa_session_token)
        setStep('2fa')
        setIsLoading(false)
        return
      }

      // No 2FA required, login complete
      navigate('/', { replace: true })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid code'
      // Check if the error indicates account lockout
      if (errorMessage.includes('locked') || errorMessage.includes('Too many failed attempts')) {
        const match = errorMessage.match(/(\d+) (?:more )?minutes?/i)
        setLockoutMinutes(match ? parseInt(match[1]) : 30)
        setStep('locked')
      } else {
        setError(errorMessage)
        setCode(['', '', '', '', '', ''])
        codeInputRefs.current[0]?.focus()
      }
    } finally {
      setIsLoading(false)
    }
  }

  // 2FA Code handlers
  const handleTwoFACodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)

    const newCode = [...twoFACode]
    newCode[index] = digit
    setTwoFACode(newCode)

    if (digit && index < 5) {
      twoFAInputRefs.current[index + 1]?.focus()
    }

    if (digit && index === 5) {
      const fullCode = newCode.join('')
      if (fullCode.length === 6) {
        handleTwoFASubmit(fullCode)
      }
    }
  }

  const handleTwoFAKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
      twoFAInputRefs.current[index - 1]?.focus()
    }
  }

  const handleTwoFAPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 6) {
      const newCode = pastedData.split('')
      setTwoFACode(newCode)
      twoFAInputRefs.current[5]?.focus()
      handleTwoFASubmit(pastedData)
    }
  }

  const handleTwoFASubmit = async (fullCode?: string) => {
    const codeToVerify = fullCode || twoFACode.join('')
    if (codeToVerify.length !== 6) {
      setError('Please enter the complete 6-digit code')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      await verify2FA(mfaSessionToken, codeToVerify)
      navigate('/', { replace: true })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid code'
      setError(errorMessage)
      setTwoFACode(['', '', '', '', '', ''])
      twoFAInputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    if (resendCooldown > 0) return

    setError('')
    setIsLoading(true)

    try {
      await resendCode(sessionToken)
      setResendCooldown(30)
      setCode(['', '', '', '', '', ''])
      codeInputRefs.current[0]?.focus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToTenants = () => {
    setStep('tenant')
    setCode(['', '', '', '', '', ''])
    setSessionToken('')
    setSelectedTenant(null)
    setError('')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-700 via-primary-600 to-primary-500 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-4">
              <img src="/logos/clarantis-icon.svg" alt="Clarantis" className="w-20 h-20" />
            </div>
            <h1 className="text-2xl font-bold text-primary-700">Clarantis</h1>
            <p className="text-gray-500 mt-1">
              {step === 'validating' && 'Verifying sign-in link...'}
              {step === 'tenant' && 'Select your organization'}
              {step === 'code' && 'Enter verification code'}
              {step === '2fa' && 'Two-factor authentication'}
              {step === 'locked' && 'Account temporarily locked'}
              {step === 'error' && 'Sign-in failed'}
            </p>
          </div>

          {/* Error Message */}
          {error && step !== 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Validating State */}
          {step === 'validating' && (
            <div className="flex flex-col items-center py-8">
              <svg
                className="animate-spin h-10 w-10 text-primary-600 mb-4"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-gray-600">Please wait...</p>
            </div>
          )}

          {/* Error State */}
          {step === 'error' && (
            <div className="space-y-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-800 font-medium">Unable to sign in</p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>

              <Link
                to="/login"
                className="w-full btn-primary py-3 text-base flex items-center justify-center"
              >
                Request a new sign-in link
              </Link>
            </div>
          )}

          {/* Account Locked State */}
          {step === 'locked' && (
            <div className="space-y-6">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
                <Lock className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">Account temporarily locked</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Your account has been locked due to too many failed sign-in attempts.
                    Please try again in <strong>{lockoutMinutes} minute{lockoutMinutes !== 1 ? 's' : ''}</strong>.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 text-center">
                  This security measure helps protect your account. The lockout will automatically expire.
                </p>
              </div>

              <div className="space-y-3">
                <p className="text-sm text-gray-500 text-center">What you can do:</p>
                <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside">
                  <li>Wait for the lockout period to expire</li>
                  <li>Contact your administrator for immediate assistance</li>
                  <li>Make sure you have the correct verification code</li>
                </ul>
              </div>

              <Link
                to="/login"
                className="w-full btn-primary py-3 text-base flex items-center justify-center"
              >
                Return to sign in
              </Link>
            </div>
          )}

          {/* Tenant Picker */}
          {step === 'tenant' && (
            <div className="space-y-6">
              {/* Email display */}
              <div className="text-center">
                <p className="text-sm text-gray-500">Signing in as</p>
                <p className="font-medium text-gray-900">{email}</p>
              </div>

              {/* Tenant list */}
              <div className="space-y-3">
                <p className="text-sm text-gray-600 text-center">
                  You have access to {tenants.length} organizations. Select one to continue:
                </p>
                {tenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => handleTenantSelect(tenant)}
                    disabled={isLoading}
                    className="w-full p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors flex items-center justify-between group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                        <Building2 className="h-5 w-5 text-primary-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">
                          {tenant.name || tenant.slug}
                        </p>
                        <p className="text-sm text-gray-500 capitalize">{tenant.role}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600" />
                  </button>
                ))}
              </div>

              {isLoading && (
                <div className="flex justify-center">
                  <svg
                    className="animate-spin h-6 w-6 text-primary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              )}
            </div>
          )}

          {/* Code Entry */}
          {step === 'code' && (
            <div className="space-y-6">
              {/* Back button (only if multiple tenants) */}
              {tenants.length > 1 && (
                <button
                  type="button"
                  onClick={handleBackToTenants}
                  className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Choose different organization
                </button>
              )}

              {/* Email and org display */}
              <div className="text-center space-y-1">
                <p className="text-sm text-gray-500">Code sent to</p>
                <p className="font-medium text-gray-900">{email}</p>
                {selectedTenant && (
                  <p className="text-sm text-primary-600">
                    {selectedTenant.name || selectedTenant.slug}
                  </p>
                )}
              </div>

              {/* Code input boxes */}
              <div className="flex justify-center gap-2">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (codeInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    onPaste={index === 0 ? handleCodePaste : undefined}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all"
                    disabled={isLoading}
                  />
                ))}
              </div>

              {/* Verify button */}
              <button
                type="button"
                onClick={() => handleCodeSubmit()}
                disabled={isLoading || code.join('').length !== 6}
                className="w-full btn-primary py-3 text-base disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Verifying...
                  </span>
                ) : (
                  'Verify Code'
                )}
              </button>

              {/* Resend code */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isLoading}
                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </div>
          )}

          {/* 2FA Code Entry */}
          {step === '2fa' && (
            <div className="space-y-6">
              {/* 2FA Info */}
              <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-2">
                  <Shield className="h-8 w-8 text-primary-600" />
                </div>
                <p className="text-sm text-gray-600">
                  Enter the 6-digit code from your authenticator app
                </p>
                <p className="text-xs text-gray-400">
                  You can also use a backup code
                </p>
              </div>

              {/* 2FA Code input boxes */}
              <div className="flex justify-center gap-2">
                {twoFACode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (twoFAInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleTwoFACodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleTwoFAKeyDown(index, e)}
                    onPaste={index === 0 ? handleTwoFAPaste : undefined}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all"
                    disabled={isLoading}
                  />
                ))}
              </div>

              {/* Verify 2FA button */}
              <button
                type="button"
                onClick={() => handleTwoFASubmit()}
                disabled={isLoading || twoFACode.join('').length !== 6}
                className="w-full btn-primary py-3 text-base disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Verifying...
                  </span>
                ) : (
                  'Verify'
                )}
              </button>

              {/* Help text */}
              <div className="text-center">
                <p className="text-xs text-gray-400">
                  Lost access to your authenticator? Contact your administrator
                </p>
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-white/80">
          Governance, Risk & Compliance Platform
        </p>
      </div>
    </div>
  )
}
